import FilterChip from 'components/Luxkit/Chips/FilterChip'
import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'
import DropdownSheet from 'components/Luxkit/Dropdown/Sheet/DropdownSheet'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { countOccupants, formatOccupantsForSelectionBox, generateRoomOccupants, getMaxGuestCount } from 'lib/offer/occupancyUtils'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import { pluralizeToString } from 'lib/string/pluralize'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import React, { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import OfferCapacityWithAgesVerticalSelection from '../../CapacityWithAges/OfferCapacityWithAgesVerticalSelection'
import OfferCapacityWithAgesVerticalSidebarSelection from '../../CapacityWithAges/OfferCapacityWithAgesVerticalSidebarSelection'

interface Props {
  offer: App.Offer | App.BedbankOffer | Tours.V2Offer;
  rooms?: Array<App.Occupants>;
  open: boolean;
  disableMultiRoom?: boolean;
  capacities?: Array<App.PackageRoomCapacity>;
  occupantUnit?: 'guest' | 'traveller';
  variant?: 'modal' | 'dropdown';
}
const defaultRooms = [generateRoomOccupants()]

function OfferCalendarCapacitySelect(props: Props) {
  const {
    offer,
    open,
    disableMultiRoom,
    capacities,
    occupantUnit = 'guest',
    rooms = defaultRooms,
    variant = 'dropdown',
  } = props
  const triggerRef = useRef(null)
  const allRooms = useMemo(() => countOccupants(rooms), [rooms])
  const isMobile = useIsMobileScreen()
  const showModal = useContext(ModalContext)
  const [localRooms, setLocalRooms] = useState<Array<App.RoomOccupants | App.Occupants>>(rooms)

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()

  const saveRooms = useCallback(() => {
    dispatch({
      type: OfferPageActions.setRooms,
      rooms: localRooms as any,
    })
  }, [dispatch, localRooms])

  const onToggleOpen = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'capacity' })
    if (open) {
      onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.CloseSubFilter, CALENDAR_GA_EVENT_LABEL.OpenCapacity)
    } else {
      onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.OpenSubFilter, CALENDAR_GA_EVENT_LABEL.OpenCapacity)
    }
  }, [dispatch, onUserAnalyticsAction, open])

  const onClose = useCallback(() => {
    saveRooms()
    dispatch({ type: OfferPageActions.closeSubFilter })
    onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.CloseSubFilter, CALENDAR_GA_EVENT_LABEL.OpenCapacity)
  }, [dispatch, onUserAnalyticsAction, saveRooms])

  const maxGuests = useMemo((): number => getMaxGuestCount(capacities), [capacities])

  useEffect(() => {
    if (open && variant === 'modal') {
      showModal(<ModalBase height="max">
        <OfferCapacityWithAgesVerticalSidebarSelection
          footerText="Apply"
        />
      </ModalBase>,
      ).then(() => {
        onClose()
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const value = useMemo(() => {
    if (rooms.length > 0) {
      const occupantsText = formatOccupantsForSelectionBox(isTourV2Offer(offer) && offer.isLeTour, allRooms)
      const roomText = disableMultiRoom ? '' : pluralizeToString(offer.saleUnit, rooms.length)
      return <><b>{occupantsText}</b>{roomText ? ` (${roomText})` : undefined}</>
    }

    return 'Select guests'
  }, [allRooms, disableMultiRoom, offer, rooms.length])

  const onDropdownSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    saveRooms()
    onClose()
  }, [onClose, saveRooms])

  const formId = useId()

  return (<>
    <FilterChip
      size="medium"
      ref={triggerRef}
      onClick={onToggleOpen}
      endIcon={variant === 'dropdown' ? <DropdownChevron open={open} /> : undefined}
      selected={open}
    >
      {value}
    </FilterChip>
    {variant === 'dropdown' && <DropdownSheet
      size="M"
      open={open && !isMobile}
      hasDismissButton
      onClose={onClose}
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      data-testid="offer-calendar-capacity-select-dropdown"
      title="Who is going?"
      subtitle={maxGuests > 0 ? `Maximum occupancy is ${pluralizeToString(occupantUnit, maxGuests)}` : undefined}
      primaryActionProps={{
        children: 'Apply',
        onClick: onClose,
      }}
    >
      <form onSubmit={onDropdownSubmit} id={formId}>
        <OfferCapacityWithAgesVerticalSelection
          capacities={capacities}
          disableMultiRoom={disableMultiRoom}
          onChange={setLocalRooms}
        />
      </form>
    </DropdownSheet>}
  </>
  )
}

export default withOfferPageState((state) => {
  return {
    offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
    rooms: state.rooms,
    open: state.activeSubFilter === 'capacity',
  }
})(OfferCalendarCapacitySelect)
